import {useRef, lazy, useState, useMemo} from "react";
import qs from "qs";
import NotFound from "../../pages/404";
import PublicProjectLayout from "./PublicProjectLayout";
import {
  XCol,
  XRow,
  Arrow,
  cx,
  XText,
  XPush,
  uiStyles,
  TooltipForChild,
  xcolors,
  useMedia,
  mq,
} from "@cdx/common";
import {
  DECKWIDTH,
  useContainerPosition,
  DECKWIDTHWOMARGIN,
  DeckInspector,
} from "../../pages/decks/DecksOverviewWithSpaces";
import {useSpring, animated} from "react-spring";
import {projectDecks} from "../../lib/utils";
import FlipGroup from "react-flip-primitives";
import {deckOverviewStyles as styles} from "../../pages/decks/decks.css";
import Deck from "../../components/Deck";
import {matchPath} from "react-router-dom";
import publicProjectRoutes from "./public-project-routes";
import Markdown from "../../components/Markdown";
import {publicProjectStyles} from "./public-project.css";
import {useMetaDescription, useSetTitle} from "../../components/Title";
import useOpenTracker from "./useOpenTracker";
import CommunityButton from "./CommunityButton";
import {useModalWithData} from "../../components/Modals";
import {getVarName} from "@vanilla-extract/private";
import {CdxCropImgByFile, CdxImgByFile} from "../../components/CdxImg";
import AgeProtection from "./AgeProtection";
import OpenFooter from "./OpenFooter";
import {useMarkdownToText} from "../../components/Markdown/markdownToText";
import cdxEnv from "../../env";
import {
  Box,
  Col,
  DSButton,
  DSIconButton,
  DSIconChevronDown,
  DSIconChevronUp,
  DSIconHistory,
  Row,
} from "@cdx/ds";
import {iconStyles} from "@cdx/ds/components/DSIcon/DSIcon.css";
import {streakIconStyles} from "../hand-queue/hand-queue.css";
import {ReactComponent as FireSvg} from "../hand-queue/imgs/fire.svg";
import {prettyDayDiff} from "../../lib/date-utils";
import ActivityFeedBar from "./ActivityFeedBar";
import dsStyles from "@cdx/ds/css/index.css";
import PublicProjectShareButton from "./ShareButton";
import {api} from "../../lib/api";
import {SpaceIcon, SpaceStats, getDeckOrderForProject} from "../../pages/decks/SpaceComponents";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import {useLocalStorageState} from "../../lib/storage";
import RegisterVisionBoard from "../vision-board/RegisterVisionBoard";

const DeckLane = ({
  decks,
  offset,
  registerFlipNode,
  selectedDeck,
  isSomeDeckSelected,
  root,
  history,
}) => {
  return (
    <XRow sp={4} ref={registerFlipNode(`batch-${offset}`, {onPresence: (val) => ({opacity: val})})}>
      {decks.map((deck) => (
        <Deck
          key={deck.id}
          deck={deck}
          isSelected={deck === selectedDeck}
          isOtherSelected={isSomeDeckSelected && deck !== selectedDeck}
          ref={registerFlipNode(deck.$meta.isLoaded ? deck.id.toString() : "none", {
            parentFlipKey: `batch-${offset}`,
            onPresence: (val) => ({opacity: val}),
            positionSpringConfig: {noPointerEvents: true},
          })}
          root={root}
          history={history}
          nonTransparentBackground
        />
      ))}
    </XRow>
  );
};

const DeckInspectorLane = ({
  selectedDeck,
  registerFlipNode,
  currentDeckIndex,
  location,
  root,
  history,
  decksContainerLeft,
}) => {
  const arrowLeft = decksContainerLeft - 8 + DECKWIDTHWOMARGIN / 2 + DECKWIDTH * currentDeckIndex;
  const scrollElementRef = useRef(window);
  return (
    <div className={cx(styles.deckInspectorBox.base, styles.deckInspectorBox.isPublic)}>
      {currentDeckIndex !== -1 && (
        <Arrow
          pointTo="top"
          size="md"
          color="white"
          ref={registerFlipNode("arrow", {onPresence: (val) => ({opacity: val})})}
          style={{left: arrowLeft}}
        />
      )}
      <DeckInspector
        location={location}
        deck={selectedDeck}
        root={root}
        history={history}
        scrollElementRef={scrollElementRef}
      />
    </div>
  );
};

const Space = ({
  location,
  root,
  history,
  decksPerRow,
  selDeckId,
  space,
  decks: outerDecks,
  isDeckOrderLoaded,
  selectedDeck,
  collapseSpaceInfo,
  decksContainerLeft,
  project,
  singleSpace,
}) => {
  const {isSpaceCollapsed, toggleSpaceCollapsed} = collapseSpaceInfo;
  const spaceCollapsed = isSpaceCollapsed(space);
  const handleCollapseClick = () => {
    toggleSpaceCollapsed(space);

    if (outerDecks.some((d) => d.id === selDeckId)) {
      history.replace(publicProjectRoutes.publicProject.getUrl(project));
    }
  };
  return (
    <FlipGroup
      changeKey={`${selDeckId ? selDeckId : "closed"}-${outerDecks.map((d) => d.id).join(",")}`}
      keysAndData={outerDecks.map((deck) => ({
        key: deck.$meta.isLoaded ? deck.id.toString() : "none",
        data: deck,
      }))}
    >
      {(registerFlipNode, keysAndData) => {
        const decks = keysAndData.map((knd) => knd.data);
        const lanes = [];
        let i = 0;
        let offset = 0;
        while (decksPerRow && decks.length) {
          const lane = decks.splice(0, decksPerRow);
          lanes.push(
            <DeckLane
              key={`lane-${(i += 1)}`}
              decks={lane}
              offset={offset}
              registerFlipNode={registerFlipNode}
              selectedDeck={selectedDeck}
              isSomeDeckSelected={!!selectedDeck}
              root={root}
              history={history}
            />
          );
          if (isDeckOrderLoaded) {
            const idx = lane.findIndex((d) => d.id === selDeckId);
            if (idx > -1) {
              const selDeck = selectedDeck.$meta.isLoaded ? selectedDeck : lane[idx];
              lanes.push(
                <DeckInspectorLane
                  key="inspector"
                  registerFlipNode={registerFlipNode}
                  selectedDeck={selDeck}
                  currentDeckIndex={idx}
                  location={location}
                  root={root}
                  history={history}
                  decksContainerLeft={decksContainerLeft}
                />
              );
            }
          }
          offset += lane.length;
        }
        return (
          <Col relative>
            {!singleSpace && (
              <>
                <div
                  className={cx(styles.spaceContainerBg.base, styles.spaceContainerBg.mode.open)}
                />
                <Row
                  sp="24px"
                  align="center"
                  pa="12px"
                  cursor="pointer"
                  userSelect="none"
                  relative
                  styleChild
                >
                  <div onClick={handleCollapseClick} data-cdx-clickable>
                    <TooltipForChild tooltip={spaceCollapsed ? "Show decks" : "Hide decks"} delayed>
                      <DSIconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCollapseClick();
                        }}
                        size="sm"
                        variant="tertiary"
                        icon={spaceCollapsed ? <DSIconChevronDown /> : <DSIconChevronUp />}
                      />
                    </TooltipForChild>
                    <Row sp="8px" align="center">
                      <SpaceIcon space={space} size={20} className={dsStyles.color.secondary} />
                      <Box size={14} color={spaceCollapsed ? "secondary" : "primary"}>
                        {space.name ?? "Default Space"}
                      </Box>
                    </Row>
                    {spaceCollapsed && <SpaceStats decks={outerDecks} />}
                  </div>
                </Row>
              </>
            )}
            {!spaceCollapsed && (
              <Col sp="12px" px="12px" pb="12px'">
                {lanes}
              </Col>
            )}
          </Col>
        );
      }}
    </FlipGroup>
  );
};

const useCollapsedSpaceInfo = (project) => {
  const [val, setVal] = useLocalStorageState("projects.collapsedSpaces", {});
  const toKey = (space) => `${project.id}:${space.id}`;
  return {
    isSpaceCollapsed: (space) => Boolean(val[toKey(space)]),
    toggleSpaceCollapsed: (space) => {
      const key = toKey(space);
      setVal({...val, [key]: !val[key]});
    },
  };
};

const ProjectDecks = ({
  selectedDeck,
  selectedDeckInfo,
  spacesAndDecks,
  isDeckOrderLoaded,
  location,
  root,
  history,
  project,
}) => {
  const decksContainerRef = useRef();
  const {width: decksContainerWidth, left: decksContainerLeft} =
    useContainerPosition(decksContainerRef);

  const decksPerRow = Math.max(1, Math.floor((decksContainerWidth + 60) / DECKWIDTH));

  const selDeckId =
    (selectedDeck && selectedDeck.$meta.get("id")) || (selectedDeckInfo && selectedDeckInfo.deckId);

  const loadStyles = useSpring({opacity: decksContainerWidth && isDeckOrderLoaded ? 1 : 0});
  const collapseSpaceInfo = useCollapsedSpaceInfo(project);

  return (
    <Col sp="12px" styleChild px="12px">
      <animated.div style={loadStyles} ref={decksContainerRef}>
        {spacesAndDecks.map(({space, decks}) => (
          <Space
            key={space.id}
            space={space}
            decks={decks}
            decksPerRow={decksPerRow}
            selDeckId={selDeckId}
            location={location}
            root={root}
            history={history}
            isDeckOrderLoaded={isDeckOrderLoaded}
            selectedDeck={selectedDeck}
            decksContainerLeft={decksContainerLeft}
            project={project}
            collapseSpaceInfo={collapseSpaceInfo}
            singleSpace={spacesAndDecks.length === 1}
          />
        ))}
      </animated.div>
    </Col>
  );
};

const HeroBg = ({project, children}) => {
  const bgFile = project.publicBackgroundImage;
  const banner = project.publicBannerFile;
  const tileFile = project.publicTileFile;

  const height = bgFile || tileFile ? 10 : 6;
  const fallbackStyle = banner &&
    banner.meta.dominantColors &&
    banner.meta.dominantColors.length > 0 && {backgroundColor: banner.meta.dominantColors[0].color};

  return (
    <XCol relative>
      <XCol absolute inset="x" style={{top: 0, height: `${height + 9}rem`}} bg="gray600">
        <CdxCropImgByFile
          width={1920}
          height={400}
          asBackground
          file={bgFile || tileFile}
          fallbackStyle={fallbackStyle}
          fullScreenWidth
          noSpinner
        />
        <XCol absolute inset="full" className={publicProjectStyles.bgGradient} />
      </XCol>
      <XCol
        align="center"
        sp={4}
        style={{paddingTop: `${height - 2}rem`, minHeight: `${height}rem`}}
        relative
      >
        {children}
      </XCol>
    </XCol>
  );
};

const ShowStreak = ({streak}) => (
  <TooltipForChild tooltip={`Streak for weekly card completion`} placement="top">
    <XRow
      rounded="full"
      bg="gray800"
      border="gray700"
      align="center"
      justify="center"
      elevation={0}
      pr={1}
      pl={0}
      sp={0}
      py={0}
    >
      <Box
        styleChild
        className={cx(
          iconStyles.mode.block,
          iconStyles.base,
          iconStyles.sizes[16],
          streakIconStyles.active
        )}
      >
        <FireSvg />
      </Box>
      <XText size={1} preset="bold" color="gray200">
        {streak}
      </XText>
    </XRow>
  </TooltipForChild>
);

const UpdatedArea = ({lastActivityAt}) => {
  if (!lastActivityAt) return null;
  const {label, dayDiff} = prettyDayDiff(lastActivityAt);
  if (dayDiff > 7) return null;
  return (
    <XCol
      rounded="full"
      {...(dayDiff <= 1
        ? {bg: "done100", border: "done300", style: {color: xcolors.done700}}
        : {bg: "gray800", border: "gray700", style: {color: xcolors.gray200}})}
      className={styles.streakIconContainer}
      align="center"
      justify="center"
      elevation={0}
      px={1}
    >
      <XText size={1} preset="bold" color={null}>
        Updated {label}
      </XText>
    </XCol>
  );
};

const NewHero = ({project}) => {
  const message = project.$meta.get("publicMessage", null);
  const banner = project.publicBannerFile;
  const {publicProjectInfo} = project;
  const cardDoneStreak = publicProjectInfo?.$meta.get("cardDoneStreak", 0);

  return (
    <HeroBg project={project}>
      <Row absolute top={0} left={0} pl={5} pt={1} sp={2}>
        {(cardDoneStreak ?? 0) > 0 && <ShowStreak streak={cardDoneStreak} />}
        {publicProjectInfo && <UpdatedArea lastActivityAt={publicProjectInfo.lastActivityAt} />}
      </Row>
      {banner && (
        <CdxImgByFile
          file={project.publicBannerFile}
          alt=""
          maxHeight={125}
          maxWidth={400}
          style={{marginTop: "-3rem"}}
          noSpinner
        />
      )}
      {message && (
        <XCol
          as={Markdown}
          pb={5}
          size="lg"
          variant="onDark"
          style={{maxWidth: "40rem", textAlign: "center"}}
        >
          {message}
        </XCol>
      )}
    </HeroBg>
  );
};

const TitleBar = ({project, root, setBarOpen, isBarOpen}) => (
  <XRow as="header" px={4} py={2} bg="gray900" elevation={2} align="center" relative>
    <XText as="h1" preset="bold" color="white" size={4}>
      {project.publicHeading}
    </XText>
    <XPush />
    <XRow align="center" sp={4}>
      <XRow align="center" sp={2}>
        <DSButton href={cdxEnv.OPEN_APP_HOST} variant="secondary" size="sm" negatePadding>
          Back <span className={uiStyles.hide.sm}>to gallery</span>
        </DSButton>
        <CommunityButton root={root} project={project} />
      </XRow>
      <Row align="center" sp="16px">
        <TooltipForChild tooltip={isBarOpen ? "Hide Project Activity" : "Show Project Activity"}>
          <DSIconButton
            icon={<DSIconHistory />}
            active={isBarOpen}
            onClick={() => setBarOpen((v) => !v)}
            variant="tertiary"
            size="sm"
            label={<span className={uiStyles.hide.md}>Activity</span>}
            negatePadding
          />
        </TooltipForChild>
        <PublicProjectShareButton project={project} />
      </Row>
    </XRow>
  </XRow>
);

const LoadedProject = ({project, root, history, location, selectedDeckSeq}) => {
  useOpenTracker({root, projectId: project.$meta.get("id", null)});
  useSetTitle(project.$meta.get("publicHeading", null), {pos: 3});
  const deckSeqToDeckInfoRef = useRef({});
  projectDecks(project).forEach((d) => {
    const seq = d.$meta.get("accountSeq", null);
    if (seq) deckSeqToDeckInfoRef.current[seq] = {deckId: d.id, projectId: project.id};
  });
  const selectedDeck =
    selectedDeckSeq &&
    project.$meta.find("decks", {accountSeq: selectedDeckSeq, isDeleted: false})[0];

  const {isLoaded: isDeckOrderLoaded, result: spacesAndDecks} = useMemo(
    () => checkIfPresent(() => getDeckOrderForProject({project}), api),
    [project]
  );
  const publicMessage = project.$meta.get("publicMessage", null);
  const message = useMarkdownToText(publicMessage);
  useMetaDescription(message);

  const isSmall = useMedia(mq.xl);

  const [barIsOpen, setIsBarOpen] = useState(false);

  return (
    <Col
      flex="auto"
      className={publicProjectStyles.container}
      style={
        project.publicBackgroundColor
          ? {[getVarName(publicProjectStyles.bgColorVar)]: project.publicBackgroundColor}
          : {}
      }
    >
      <TitleBar project={project} root={root} setBarOpen={setIsBarOpen} isBarOpen={barIsOpen} />
      <Row flex="auto" relative>
        <Col flex="auto" minWidth={0}>
          <NewHero project={project} />
          <ProjectDecks
            project={project}
            root={root}
            history={history}
            selectedDeckInfo={selectedDeckSeq && deckSeqToDeckInfoRef.current[selectedDeckSeq]}
            selectedDeck={selectedDeck}
            spacesAndDecks={spacesAndDecks}
            isDeckOrderLoaded={isDeckOrderLoaded}
            location={location}
          />
          <XPush />
          <OpenFooter publicPath={project.publicPath} />
        </Col>
        <ActivityFeedBar open={barIsOpen} project={project} isSmall={isSmall} root={root} />
      </Row>
    </Col>
  );
};

const ConfirmTokenModal = lazy(
  () => import(/* webpackChunkName: "ConfirmTokenModal" */ "./ConfirmTokenModal")
);
const PasswordResetModal = lazy(
  () => import(/* webpackChunkName: "PasswordResetModal" */ "./PasswordResetModal")
);

const PublicProject = (props) => {
  const {params, root, history, location} = props;

  const {publicPath} = params;
  const project =
    publicPath &&
    root.account.$meta.first("projects", {isPublic: true, publicPath: publicPath, $order: "id"});

  const {registrationToken, show, resetToken, ...restSearch} = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const handleModalClose = () => {
    history.replace({
      ...location,
      search: qs.stringify({
        ...restSearch,
        registrationToken: undefined,
        resetToken: undefined,
        show: undefined,
      }),
    });
  };
  const renderRegistrationModal = useModalWithData(registrationToken, {
    hideClose: true,
    cdxContent: "registrationTokenModal",
    width: 400,
  });
  const renderPasswordResetModal = useModalWithData(resetToken, {
    hideClose: true,
    cdxContent: "passwordResetModal",
    width: 400,
  });

  if (!project || project.$meta.isDeleted()) {
    return <NotFound {...props} />;
  }
  const match = matchPath(location.pathname, {
    path: publicProjectRoutes.publicProjectWithDeck.path,
  });
  const selectedDeckSeq = match && match.params.deckId && parseInt(match.params.deckId, 10);

  return (
    <PublicProjectLayout location={location}>
      <LoadedProject
        project={project}
        root={root}
        history={history}
        selectedDeckSeq={selectedDeckSeq}
        location={location}
      />
      {project && project.publicIsExplicit && <AgeProtection />}
      {renderRegistrationModal((token) => (
        <ConfirmTokenModal token={token} onClose={handleModalClose} />
      ))}
      {renderPasswordResetModal((token) => (
        <PasswordResetModal token={token} onClose={handleModalClose} />
      ))}
      <RegisterVisionBoard location={location} history={history} root={root} />
    </PublicProjectLayout>
  );
};

export default PublicProject;
